// crewbit-admin/src/components/schedule/DayCanvas.tsx
//
// Schedule Module Phase C C1.5 — top-level Day canvas orchestrator.
// Body parallel to mobile crewbit-mobile/components/schedule/DayCanvas.tsx
// modulo runtime per feedback_admin_pair_must_match_mobile_body.md.
//
// Spec: docs/references/schedule/SCHEDULE_MODULE_SPEC_v1.md §2 + §C.6.

import { admin } from '../../styles/tokens';
import type { ScheduleEntry } from '../../types/schedule';
import type { ConflictDetail } from '../../lib/errorMap';
import type { DayLayoutMode } from '../../lib/scheduleLayout';
import { ConflictBanner } from './ConflictBanner';
import { DayGrid } from './DayGrid';
import { DayHeader } from './DayHeader';
import type { ResourceColumnOperator } from './ResourceColumn';

export interface DayCanvasProps {
  date: string;
  industry?: string;
  routeLensEnabled?: boolean;
  travelBufferMinutes: number;
  layoutMode: DayLayoutMode;
  entries: ScheduleEntry[];
  operators?: ResourceColumnOperator[];

  selectedEntryId?: string;
  onEntryTap: (entryId: string) => void;
  onEntryLongPress?: (entryId: string) => void;
  onResourceDrop?: (droppedEntryId: string, newResourceId: string) => void;

  conflicts?: ConflictDetail[] | null;
  conflictRpcName?: string;
  onConflictOverride?: () => Promise<void>;
  onConflictDismiss?: () => void;

  dayStartHour?: number;
  dayEndHour?: number;

  onPrevDay?: () => void;
  onNextDay?: () => void;
  onToday?: () => void;
}

export function DayCanvas({
  date,
  routeLensEnabled,
  travelBufferMinutes,
  layoutMode,
  entries,
  operators,
  onEntryTap,
  onEntryLongPress,
  onResourceDrop,
  conflicts,
  conflictRpcName,
  onConflictOverride,
  onConflictDismiss,
  dayStartHour,
  dayEndHour,
  onPrevDay,
  onNextDay,
  onToday,
}: DayCanvasProps) {
  const showTravelSlivers = routeLensEnabled === true;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      flex: 1, height: '100%',
      backgroundColor: admin.bg,
    }}>
      <DayHeader
        date={date}
        layoutMode={layoutMode}
        onPrev={onPrevDay}
        onNext={onNextDay}
        onToday={onToday}
      />

      {conflicts && conflicts.length > 0 && onConflictOverride && onConflictDismiss && conflictRpcName ? (
        <ConflictBanner
          conflicts={conflicts}
          pendingMutationContext={{
            rpcName: conflictRpcName,
            onOverride: onConflictOverride,
          }}
          onDismiss={onConflictDismiss}
        />
      ) : null}

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <DayGrid
          date={date}
          layoutMode={layoutMode}
          entries={entries}
          travelBufferMinutes={travelBufferMinutes}
          operators={operators}
          onEntryTap={onEntryTap}
          onEntryLongPress={onEntryLongPress}
          onResourceDrop={onResourceDrop}
          showTravelSlivers={showTravelSlivers}
          dayStartHour={dayStartHour}
          dayEndHour={dayEndHour}
        />
      </div>
    </div>
  );
}
